import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, Loader2, LogOut, ArrowLeft, Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AdminEvents } from "@/components/AdminEvents";
import { AdminRedaction } from "@/components/AdminRedaction";
import { AdminGalerie } from "@/components/AdminGalerie";
import { NewsletterWyrd } from "@/components/AdminNewsletter";
import { AdminSoins } from "@/components/AdminSoins";
import { AdminParticipants } from "@/components/AdminParticipants";
import { AdminTelegram } from "@/components/AdminTelegram";
import { AdminDisponibilites } from "@/components/AdminDisponibilites";
import { AdminTranslations } from "@/components/AdminTranslations";
import { AdminFormations } from "@/components/AdminFormations";
import { AdminBoutique } from "@/components/AdminBoutique";
import { AdminTambourFunnel } from "@/components/AdminTambourFunnel";
import { AdminStagePricing } from "@/components/AdminStagePricing";
import { SECTIONS, resolveLocation } from "@/components/admin/nav";
import { API, authHeaders, btn, errMsg, inputCls } from "@/components/admin/ui";
import { GlobalSearch } from "@/components/admin/GlobalSearch";
import { Dashboard } from "@/components/admin/Dashboard";
import { Calendrier } from "@/components/admin/Calendrier";
import { Reservations } from "@/components/admin/Reservations";
import { ReservationDetail } from "@/components/admin/ReservationDetail";
import { Prestations } from "@/components/admin/Prestations";
import { FichesClients } from "@/components/admin/FichesClients";
import { Commandes, Exports, TransactionsStripe } from "@/components/admin/Finances";
import { CartesCadeaux } from "@/components/admin/CartesCadeaux";
import { Abonnes, Lettre, Messages } from "@/components/admin/Communication";
import { Administrateurs, Integrations, Journal, Seo } from "@/components/admin/Parametres";
import { CodesPromo } from "@/components/admin/CodesPromo";
import { useNoIndex, useSeo } from "@/lib/seo";

const Section = ({ title, children }) => <div><h2 className="font-serif text-2xl sm:text-3xl text-bark mb-6">{title}</h2>{children}</div>;

const Nav = ({ section, vue, go }) => (
  <nav className="space-y-1" aria-label="Administration" data-testid="admin-sidebar">
    {SECTIONS.map(({ id, label, icon: Icon, items }) => (
      <div key={id}>
        <button type="button" onClick={() => go(id, items[0][0])} data-testid={`admin-section-${id}`} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors ${section === id ? "bg-earth text-sand" : "text-bark hover:bg-sand-dark"}`}><Icon className="w-4 h-4 shrink-0" />{label}</button>
        {section === id && items.length > 1 && <div className="ml-6 mt-1 mb-2 border-l border-sand-dark">{items.map(([vid, vlabel]) => <button type="button" key={vid} onClick={() => go(id, vid)} data-testid={`admin-vue-${id}-${vid}`} className={`block w-full text-left pl-3 pr-2 py-1.5 text-xs transition-colors ${vue === vid ? "text-terra font-medium" : "text-fumee hover:text-bark"}`}>{vlabel}</button>)}</div>}
      </div>
    ))}
  </nav>
);

const Admin = () => {
  useSeo({ title: "Administration · Entre Les Mondes", description: "", noindex: true });
  useNoIndex();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [menu, setMenu] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [section, vue] = resolveLocation(params, pathname);
  const client = params.get("client");
  const type = params.get("type") || "";

  const [denied, setDenied] = useState(false);
  const checkMe = (afterLogin) => axios.get(`${API}/auth/me`, authHeaders()).then((r) => { setUser(r.data); setDenied(false); }).catch((e) => { setUser(false); setDenied(afterLogin && e.response?.status === 403); });
  useEffect(() => { checkMe(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { document.title = "Espace privé — Entre les Mondes"; }, []);

  const go = (s, v, extra = {}) => { setMenu(false); navigate(`/admin?${new URLSearchParams({ section: s, vue: v, ...extra })}`); window.scrollTo({ top: 0 }); };
  const openClient = (id) => { setReservation(null); go("clients", "fiches", { client: id }); };
  const openSearch = (group, row) => {
    if (group === "clients") openClient(row.id);
    else if (group === "reservations") setReservation(row.id);
    else if (group === "sessions") navigate(`/admin/participants/${row.id}`);
    else if (group === "produits") go("boutique", "catalogue");
    else go("boutique", "commandes");
  };
  const logout = async () => { await axios.post(`${API}/auth/logout`, {}, authHeaders()).catch(() => {}); localStorage.removeItem("elm_admin_token"); setUser(false); };

  if (user === null) return <div className="pt-44 pb-32 text-center"><Loader2 className="w-8 h-8 text-terra mx-auto animate-spin" /></div>;
  if (!user) return <Login onLogin={() => checkMe(true)} denied={denied} />;

  const views = {
    "tableau/synthese": <Dashboard go={go} openReservation={setReservation} />,
    "agenda/calendrier": <Calendrier go={go} openReservation={setReservation} />,
    "agenda/sessions": <Section title="Sessions programmées"><AdminEvents typeFilter={type} /></Section>,
    "agenda/reservations": <Reservations key={params.toString()} preset={{ ...(params.get("periode") ? { periode: params.get("periode") } : {}), ...(params.get("a_confirmer") ? { a_confirmer: "1" } : {}), ...(params.get("statut_actif") ? { actives: "1" } : {}) }} onOpen={setReservation} />,
    "agenda/inscriptions": <Section title="Inscriptions par session"><AdminParticipants /></Section>,
    "agenda/rendez-vous": <Reservations key="rdv" title="Rendez-vous individuels" preset={{ type: "rendez-vous", periode: "avenir" }} onOpen={setReservation} testid="admin-rendez-vous" />,
    "agenda/disponibilites": <Section title="Disponibilités"><AdminDisponibilites /></Section>,
    "activites/prestations": <Prestations openSessions={(t) => go("agenda", "sessions", { type: t })} />,
    "activites/tambours": <Section title="Création de tambours"><div className="space-y-4"><AdminTambourFunnel /><div className="flex flex-wrap gap-2"><button type="button" className={btn} onClick={() => go("agenda", "sessions", { type: "stage" })} data-testid="admin-tambours-sessions">Dates de stages</button><button type="button" className={btn} onClick={() => go("finances", "tarifs")} data-testid="admin-tambours-tarifs">Tarifs & suppléments</button><button type="button" className={btn} onClick={() => go("agenda", "rendez-vous")}>Stages individuels</button></div></div></Section>,
    "activites/soins": <Section title="Soins énergétiques"><AdminSoins /></Section>,
    "activites/formations": <AdminFormations />,
    "boutique/catalogue": <AdminBoutique />,
    "boutique/commandes": <Commandes />,
    "clients/fiches": <FichesClients selected={client} onSelect={(id) => go("clients", "fiches", { client: id })} onOpenReservation={setReservation} />,
    "clients/wyrd": <Section title="Membres Wyrd"><NewsletterWyrd onChange={() => {}} /></Section>,
    "finances/paiements": <Reservations key="pay" title="Paiements à suivre" preset={{ paiement: "a_encaisser" }} onOpen={setReservation} testid="admin-paiements" />,
    "finances/stripe": <TransactionsStripe />,
    "finances/tarifs": <Section title="Tarifs & suppléments"><AdminStagePricing onSaved={() => {}} /></Section>,
    "finances/cartes": <CartesCadeaux />,
    "finances/codes-promo": <CodesPromo openReservation={setReservation} />,
    "finances/exports": <Exports />,
    "site/redaction": <Section title="Rédaction"><AdminRedaction /></Section>,
    "site/traductions": <Section title="Traductions"><AdminTranslations /></Section>,
    "site/mediatheque": <Section title="Médiathèque"><AdminGalerie /></Section>,
    "site/seo": <Seo />,
    "communication/messages": <Messages />,
    "communication/lettre": <Lettre />,
    "communication/abonnes": <Abonnes />,
    "communication/notifications": <Section title="Notifications Telegram"><AdminTelegram /></Section>,
    "parametres/administrateurs": <Administrateurs />,
    "parametres/integrations": <Integrations />,
    "parametres/journal": <Journal />,
  };
  const current = SECTIONS.find((s) => s.id === section);

  return (
    <div className="pt-24 pb-16 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 py-4 mb-4 border-b border-sand-dark">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setMenu(true)} className={`${btn} lg:hidden`} data-testid="admin-mobile-menu" aria-label="Menu administration"><Menu className="w-4 h-4" /></button>
          <div><p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fumee">Administration · Entre Les Mondes</p><p className="text-sm text-bark" data-testid="admin-breadcrumb">{current.label}{current.items.length > 1 ? ` › ${current.items.find(([id]) => id === vue)[1]}` : ""}</p></div>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <GlobalSearch onOpen={openSearch} />
          <Link to="/compte" data-testid="admin-back-account" className={btn}><ArrowLeft className="w-3.5 h-3.5" /> Mon espace</Link>
          <button onClick={logout} data-testid="admin-logout" className={btn}><LogOut className="w-3.5 h-3.5" /> Quitter</button>
        </div>
      </div>
      <div className="grid lg:grid-cols-[15.5rem_minmax(0,1fr)] gap-6">
        <aside className="hidden lg:block sticky top-28 h-fit max-h-[calc(100vh-8rem)] overflow-y-auto"><Nav section={section} vue={vue} go={go} /></aside>
        <main className="min-w-0" data-testid={`admin-view-${section}-${vue}`}>{views[`${section}/${vue}`]}</main>
      </div>
      {menu && <div className="fixed inset-0 z-[70] lg:hidden" data-testid="admin-mobile-drawer"><div className="absolute inset-0 bg-earth/40" onClick={() => setMenu(false)} /><div className="absolute left-0 top-0 bottom-0 w-[82vw] max-w-xs bg-sand p-4 overflow-y-auto"><div className="flex justify-end mb-3"><button type="button" onClick={() => setMenu(false)} className={btn} aria-label="Fermer"><X className="w-4 h-4" /></button></div><Nav section={section} vue={vue} go={go} /></div></div>}
      <ReservationDetail id={reservation} onClose={() => setReservation(null)} onOpenClient={openClient} />
    </div>
  );
};

const Login = ({ onLogin, denied }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form, { withCredentials: true });
      localStorage.setItem("elm_admin_token", data.token);
      onLogin(data);
    } catch (err) { setError(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <div className="pt-44 pb-32 max-w-md mx-auto px-6">
      <Lock className="w-8 h-8 text-terra mb-6" />
      <p className="eyebrow mb-3">Espace privé</p>
      <h1 className="font-serif text-4xl text-bark mb-8">Bonjour Ludovic.</h1>{denied && <p className="text-sm text-destructive mb-4" data-testid="admin-access-denied">Ce compte n’a pas accès à l’administration.</p>}
      <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
        <input data-testid="admin-login-email" type="email" required placeholder="Courriel" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
        <input data-testid="admin-login-password" type="password" required placeholder="Mot de passe" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
        {error && <p data-testid="admin-login-error" className="text-sm text-destructive">{error}</p>}
        <button type="submit" data-testid="admin-login-submit" disabled={loading} className="w-full py-4 rounded-full bg-earth text-sand text-sm hover:bg-terra hover:text-earth transition-colors disabled:opacity-60 inline-flex justify-center items-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Entrer
        </button>
      </form>
    </div>
  );
};


export default Admin;
